"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  LogIn,
  Loader2,
  Sparkles,
  Shield,
  Zap,
  CheckCircle,
  ArrowRight,
  X
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

export default function LoginPage() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    remember: false
  });
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg("");

    try {
      const { error } = await supabase.auth.signInWithPassword(
        {
          email: formData.email,
          password: formData.password,
        },
        {
          // persistSession steuert "angemeldet bleiben"
          persistSession: formData.remember
        }
      );

      if (error) {
        setErrorMsg(error.message || "Fehler beim Login");
        setIsLoading(false);
        return;
      }

      // Login erfolgreich → Dashboard
      router.push("/inbox");

    } catch (err) {
      console.error(err);
      setErrorMsg("Serverfehler");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center p-4 relative">
      {/* Dynamische Fehler-Notification oben rechts */}
      {errorMsg && (
        <div className="absolute top-5 right-5 bg-red-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 animate-fade-in">
          <X className="w-5 h-5 cursor-pointer" onClick={() => setErrorMsg("")} />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid-gray-100/50 bg-[size:20px_20px] [mask-image:radial-gradient(ellipse_at_center,white,transparent_75%)]" />

      {/* Main Container */}
      <div className="relative w-full max-w-6xl flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
        {/* Left Side */}
        <div className="hidden lg:block flex-1 space-y-8 text-center lg:text-left">
          {/* Branding & Features bleiben unverändert */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#dbb610]/10 rounded-full">
            <div className="w-2 h-2 bg-[#dbb610] rounded-full animate-pulse" />
            <span className="text-sm font-medium text-[#dbb610]">Willkommen zurück</span>
          </div>

          <div className="space-y-4">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900">
              Dein CRM für
              <span className="block text-[#dbb610] mt-2">erfolgreiche Kaltakquise</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-md mx-auto lg:mx-0">
              Verwalte deine Leads, Termine und Abschlüsse an einem Ort. Clean, schnell und effizient.
            </p>
          </div>

          {/* Feature List */}
          <div className="space-y-4 max-w-sm mx-auto lg:mx-0">
            {[
              { icon: <Zap className="w-4 h-4" />, text: "Blitzschnelle Performance" },
              { icon: <Shield className="w-4 h-4" />, text: "Enterprise-Level Sicherheit" },
              { icon: <Sparkles className="w-4 h-4" />, text: "Intelligentes CRM" },
            ].map((feature, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 p-3 bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-8 h-8 bg-[#dbb610]/10 rounded-lg flex items-center justify-center text-[#dbb610]">
                  {feature.icon}
                </div>
                <span className="text-gray-700">{feature.text}</span>
                <CheckCircle className="w-5 h-5 text-green-500 ml-auto" />
              </div>
            ))}
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex-1 w-full max-w-md">
          {/* Mobile Willkommen */}
          <div className="lg:hidden text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#dbb610]/10 rounded-full mb-3">
              <div className="w-2 h-2 bg-[#dbb610] rounded-full animate-pulse" />
              <span className="text-sm font-medium text-[#dbb610]">CRM System</span>
            </div>
            <h2 className="text-2xl font-semibold text-gray-900">Anmelden</h2>
          </div>

          {/* Login Form */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-100 shadow-2xl p-6 sm:p-8 space-y-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Field */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 block">
                  E-Mail
                </label>
                <div className="relative group">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-[#dbb610] transition-colors" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="max@unternehmen.de"
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#dbb610]/20 focus:border-[#dbb610] transition-all"
                    required
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 block">
                  Passwort
                </label>
                <div className="relative group">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-[#dbb610] transition-colors" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="••••••••"
                    className="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#dbb610]/20 focus:border-[#dbb610] transition-all"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.remember}
                    onChange={(e) => setFormData({...formData, remember: e.target.checked})}
                    className="w-4 h-4 rounded border-gray-300 text-[#dbb610] focus:ring-[#dbb610]/20"
                  />
                  <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                    Angemeldet bleiben
                  </span>
                </label>
                <Link 
                  href="/forgot-password"
                  className="text-sm text-[#dbb610] hover:text-[#c4a30e] transition-colors"
                >
                  Passwort vergessen?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full relative group overflow-hidden rounded-xl bg-[#dbb610] hover:bg-[#c4a30e] transition-all duration-300 shadow-lg shadow-[#dbb610]/20 hover:shadow-xl hover:shadow-[#dbb610]/30 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                
                <div className="relative flex items-center justify-center gap-2 px-6 py-3 text-white font-medium">
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Wird eingeloggt...</span>
                    </>
                  ) : (
                    <>
                      <LogIn className="w-5 h-5" />
                      <span>Einloggen</span>
                    </>
                  )}
                </div>
              </button>
            </form>

            {/* Register Link */}
            <div className="text-center pt-2">
              <p className="text-sm text-gray-500">
                Noch kein Konto?{" "}
                <Link 
                  href="/register"
                  className="text-[#dbb610] hover:text-[#c4a30e] font-medium transition-colors inline-flex items-center gap-1 group"
                >
                  Registrieren
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </p>
            </div>
          </div>

          {/* Footer Links */}
          <div className="flex items-center justify-center gap-4 mt-6 text-xs text-gray-400">
            <Link href="/impressum" className="hover:text-gray-600 transition-colors">
              Impressum
            </Link>
            <span>•</span>
            <Link href="/datenschutz" className="hover:text-gray-600 transition-colors">
              Datenschutz
            </Link>
          </div>

          {/* Trust Badge */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <Shield className="w-3 h-3 text-green-500" />
            <span className="text-xs text-gray-400">SSL-gesichert</span>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}